/* Preview focus board. Reads the saved activation chain; its only automatic
   write fills remaining Preview drafts after source=first_post exists. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icons.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useFreePreview } from '../../contexts/FreePreviewContext.jsx';
import { api } from '../../services/api/client.js';
import { freePublishingState } from '../../lib/freePublishing.js';
import { freeOnboardingDestination, FREE_ONBOARDING_PATH, isFirstPostOnboarding } from '../../lib/freeOnboarding.js';
import FirstPostExperience from './FirstPostExperience.jsx';
import { missingBrandFields } from '../../lib/activationProgress.js';
import { useEmailVerificationActions } from '../../lib/useEmailVerificationActions.js';
import { deriveBoardState, marketSnapshot, minutesSince, pickIdeaTitles, previewDraftsFromRows, shouldRequestPreviewDrafts, siteFacts } from '../../lib/freePreview.js';
import { trackPreview } from '../../lib/freePreviewEvents.js';
import { platformDisplayName } from '../../lib/platforms.js';

const POLL_MS = 15000;
const RETURNING_GAP_MS = 30 * 60 * 1000;
function unwrap(res) { return res?.data ?? res; }
function readLocal(key) { try { return window.localStorage.getItem(key); } catch { return null; } }
function writeLocal(key, value) { try { window.localStorage.setItem(key, value); } catch { /* display only */ } }
function readSession(key) { try { return window.sessionStorage.getItem(key); } catch { return null; } }
function writeSession(key, value) { try { window.sessionStorage.setItem(key, value); } catch { /* display only */ } }
let fallbackRequestedThisPage = false;
export function __resetPreviewFallbackForTests() { fallbackRequestedThisPage = false; }

export function useFreePreviewBoardData() {
  const freePreview = useFreePreview();
  const verifyMode = freePreview.mode === 'verify';
  const [brand, setBrand] = useState({ status: 'loading', profile: null });
  const [research, setResearch] = useState({ loaded: false, latest: null, report: null });
  const [content, setContent] = useState({ loaded: false, drafts: [] });
  const [strategy, setStrategy] = useState(null);
  const [packReason, setPackReason] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const reportIdRef = useRef(null);
  const loadBrand = useCallback(async () => {
    try {
      const res = unwrap(await api.profile.getBrandProfile());
      setBrand({ status: 'present', profile: res?.brandProfile || res || null });
    } catch (err) { setBrand({ status: err?.status === 404 ? 'missing' : 'unknown', profile: null }); }
  }, []);
  const loadResearch = useCallback(async () => {
    try {
      const list = unwrap(await api.research.list());
      const latest = (list?.reports || [])[0] || null;
      let report = null;
      if (latest?.status === 'ready') {
        if (reportIdRef.current?.id === latest.id) report = reportIdRef.current.report;
        else {
          const full = unwrap(await api.research.get(latest.id));
          report = full?.report || null;
          reportIdRef.current = { id: latest.id, report };
        }
      }
      setResearch({ loaded: true, latest, report });
    } catch { setResearch((prev) => ({ ...prev, loaded: true })); }
  }, []);
  const loadContent = useCallback(async () => {
    try {
      const res = unwrap(await api.content.list({ limit: 20 }));
      setContent({ loaded: true, drafts: previewDraftsFromRows(res?.content || res) });
    } catch { setContent((prev) => ({ ...prev, loaded: true })); }
  }, []);
  useEffect(() => {
    loadBrand();
    if (verifyMode) return;
    loadResearch();
    loadContent();
  }, [verifyMode, loadBrand, loadResearch, loadContent]);
  const createdAt = brand.profile?.createdAt;
  const updatedAt = brand.profile?.updatedAt;
  const activationAt = Date.parse(updatedAt) >= (Date.parse(createdAt) || 0) ? updatedAt : createdAt;
  const minutesSinceComplete = minutesSince(activationAt, now);
  const brandIncomplete = brand.status === 'present' && missingBrandFields(brand.profile).length > 0;
  const state = deriveBoardState({
    mode: freePreview.mode || 'active', brand: brand.status === 'missing' ? 'missing' : 'present',
    brandIncomplete, packReason, research: research.latest, drafts: content.drafts,
    limit: freePreview.drafts?.limit || 3, minutesSinceComplete, hasStrategy: Boolean(strategy),
  });
  useEffect(() => {
    if (!state.polling || brand.status === 'loading') return undefined;
    const timer = setInterval(() => {
      api.invalidateCache?.(['/api/research', '/api/content']);
      setNow(Date.now());
      loadResearch();
      loadContent();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [state.polling, brand.status, loadResearch, loadContent]);
  useEffect(() => {
    if (!verifyMode) return undefined;
    const onFocus = () => { freePreview.refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [verifyMode, freePreview]);
  useEffect(() => {
    if (state.key !== 'B5' || strategy) return;
    let alive = true;
    api.strategy.get().then((res) => { if (alive) setStrategy(unwrap(res) || {}); }).catch(() => { if (alive) setStrategy({}); });
    return () => { alive = false; };
  }, [state.key, strategy]);
  useEffect(() => {
    if (!content.loaded || !research.loaded || brand.status !== 'present') return;
    if (!content.drafts.some(draft => draft.source === 'first_post')) return;
    if (!shouldRequestPreviewDrafts({
      mode: freePreview.mode, readyCount: content.drafts.length, limit: freePreview.drafts?.limit || 3,
      research: research.latest, brandIncomplete, minutesSinceComplete, alreadyRequested: fallbackRequestedThisPage,
    })) return;
    fallbackRequestedThisPage = true;
    api.content.previewDrafts({ brandProfileId: brand.profile?.id || undefined })
      .then((res) => { if (unwrap(res)?.reason === 'brand_incomplete') setPackReason('brand_incomplete'); })
      .catch(() => { /* next poll shows the server state */ });
  }, [content.loaded, content.drafts, research.loaded, research.latest, brand.status, brand.profile, brandIncomplete, minutesSinceComplete, freePreview.mode, freePreview.drafts]);
  return { freePreview, brand, research, content, strategy, state, minutesSinceComplete, brandIncomplete };
}

function Eyebrow({ t }) { return <span className="fpb-eyebrow">{t('freePreview.eyebrow')}</span>; }
function SiteCard({ facts, t }) {
  const rows = [['siteVoice', facts.voice], ['siteAudience', facts.audience], ['siteOffer', facts.offer]].filter(([, value]) => value);
  return <section className="card fpb-card fpb-site">
    <h3 className="fpb-card-h">{t('freePreview.siteTitle')}</h3>
    <ul className="fpb-facts">{rows.map(([key, value]) => <li key={key}><Icon name="check" size={12} /> <span className="fpb-fact-l">{t(`freePreview.${key}`)}</span> <span className="fpb-fact-v">{value}</span></li>)}</ul>
  </section>;
}
function MarketCard({ snapshot, report, state, t, onOpen }) {
  const market = marketSnapshot(report);
  return <section className="card fpb-card fpb-market">
    <h3 className="fpb-card-h">{t('freePreview.snapshotTitle')}</h3>
    {snapshot === 'ready' && market.segments.length > 0 && <p className="fpb-segments">{market.segments.map((s) => (s.share !== null ? `${s.label} ${Math.round(s.share)}%` : s.label)).join(' · ')}</p>}
    {snapshot === 'ready' && market.sourceCount > 0 && <p className="fpb-muted">{t('freePreview.snapshotSources', { count: market.sourceCount })}</p>}
    {snapshot === 'failed' && <p className="fpb-muted">{t('freePreview.snapshotFailed')}</p>}
    {(snapshot === 'none' || snapshot === 'running') && <><div className="sj-sk" style={{ width: '90%' }} /><div className="sj-sk" style={{ width: '60%' }} /></>}
    {snapshot !== 'failed' && <p className="fpb-muted fpb-small">{t('freePreview.snapshotMore')}</p>}
    {(snapshot === 'ready' || state.key === 'B5') && snapshot !== 'failed' && <button type="button" className="btn btn-ghost btn-sm fpb-open" onClick={onOpen}>{t('freePreview.snapshotOpen')} <Icon name="arrowRight" size={12} /></button>}
  </section>;
}
function DraftCard({ draft, index, t, onOpen, failed }) {
  if (!draft) return <article className="card fpb-draft is-pending" aria-busy={!failed}>
    <div className="fpb-draft-placeholder" aria-hidden="true"><Icon name="edit" size={18} /></div>
    <p className="fpb-muted">{failed ? t('freePreview.draftFailed') : t('freePreview.draftWriting')}</p>
    {!failed && <><div className="sj-sk" style={{ width: '85%' }} /><div className="sj-sk" style={{ width: '55%' }} /></>}
  </article>;
  const lines = draft.body.split('\n').filter(Boolean);
  if (lines[0] === draft.title) lines.shift();
  const excerpt = lines.join('\n');
  const hasImage = Boolean(draft.imageUrl && !draft.isText);
  return <article className={`card fpb-draft${hasImage ? '' : ' fpb-draft--text'}`}>
    {hasImage && <div className="fpb-draft-media" style={{ backgroundImage: `url(${draft.imageUrl})` }} aria-hidden="true" />}
    <div className="fpb-draft-tags">{draft.platform && <span className="pill">{platformDisplayName(draft.platform)}</span>}</div>
    <h4 className="fpb-draft-h">{draft.title}</h4>
    {excerpt && <p className="fpb-draft-b">{excerpt}</p>}
    <button type="button" className="btn btn-secondary btn-sm fpb-draft-open" onClick={() => onOpen(draft, index)}>{t('freePreview.draftOpen')} <Icon name="arrowRight" size={12} /></button>
  </article>;
}
const STEP_KEYS = ['stepScan', 'stepSnapshot', 'stepPlan', 'stepDrafts'];

export default function FreePreviewBoard() {
  const preview = useFreePreview();
  const { user } = useAuth() || {};
  // Verification keeps its existing contract. The explicit new experiment must
  // never mount the legacy board's automatic drafts-2–3 writer.
  if (isFirstPostOnboarding(preview.entitlements) && ['active', 'readOnly'].includes(preview.mode)) {
    return <FirstPostExperience key={`${user?.id}:${user?.organizationId}`} />;
  }
  return <LegacyPreviewBoard />;
}

function LegacyPreviewBoard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const { freePreview, brand, research, content, strategy, state, minutesSinceComplete } = useFreePreviewBoardData();
  const { resend, sending, sent } = useEmailVerificationActions();
  const [returning, setReturning] = useState(false);
  const facts = siteFacts(brand.profile);
  const brandName = String(brand.profile?.businessName || '').trim();
  const drafts = content.drafts;
  const limit = freePreview.drafts?.limit || 3;
  const ownPublishing = Boolean(freePublishingState(freePreview.entitlements));
  const plansCta = t(ownPublishing ? 'freePublishing.viewPlans' : 'freePreview.trialCta');
  const ideas = useMemo(() => (state.key === 'B5' ? pickIdeaTitles(strategy, drafts, 3) : []), [state.key, strategy, drafts]);
  const shownStatesRef = useRef(new Set());
  const loading = brand.status === 'loading' || (freePreview.mode !== 'verify' && (!content.loaded || !research.loaded));
  const onboardingDestination = freeOnboardingDestination(freePreview.entitlements);
  const continueOnboarding = () => { if (onboardingDestination) navigate(onboardingDestination); };
  useEffect(() => {
    const userKey = user?.id || 'anon';
    const seenKey = `dathent.freePreview.seen.${userKey}`;
    const sessionKey = `dathent.freePreview.session.${userKey}`;
    const seenAt = Number(readLocal(seenKey));
    if (!readSession(sessionKey) && Number.isFinite(seenAt) && seenAt > 0 && Date.now() - seenAt > RETURNING_GAP_MS) setReturning(true);
    writeSession(sessionKey, '1');
    writeLocal(seenKey, String(Date.now()));
  }, [user?.id]);
  useEffect(() => {
    if (loading || shownStatesRef.current.has(state.key)) return;
    const first = shownStatesRef.current.size === 0;
    shownStatesRef.current.add(state.key);
    const minutes = Number.isFinite(minutesSinceComplete) ? Math.round(minutesSinceComplete) : null;
    if (first) {
      trackPreview('preview_board_viewed', { state: state.key, draftsReady: state.readyCount, minutesSinceComplete: minutes });
      if (returning) {
        const created = Date.parse(user?.createdAt || '');
        trackPreview('preview_returned', { daysSinceSignup: Number.isFinite(created) ? Math.floor((Date.now() - created) / 86400000) : null, state: state.key });
      }
    }
    if (state.key === 'BV') trackPreview('preview_verify_shown', {});
    if (state.key === 'BB') trackPreview('preview_brand_incomplete_shown', { reason: 'brand_incomplete' });
    if (state.key === 'B7') trackPreview('preview_slow_shown', { stage: state.snapshot });
    if (state.key === 'B8') trackPreview('preview_failed_shown', { stage: state.snapshot });
  }, [loading, state.key, state.readyCount, state.snapshot, minutesSinceComplete, returning, user?.createdAt]);
  const openWall = (source = 'cta', meta = {}) => freePreview.openTrialWall(source, meta);
  const openDraft = (draft, index) => {
    trackPreview('preview_draft_opened', { index, from: 'board' });
    navigate(draft.source === 'first_post' && !freePublishingState(freePreview.entitlements)
      ? `/app/reveal/${encodeURIComponent(draft.id)}` : `/app/post/${encodeURIComponent(draft.id)}`);
  };
  const openSnapshot = () => { trackPreview('preview_snapshot_opened', { from: 'board' }); navigate('/app/research'); };
  const connect = () => { trackPreview('preview_connect_clicked', { from: 'board' }); navigate('/app/settings?tab=accounts'); };
  const startNow = <button type="button" className="fpb-link" onClick={() => openWall('cta')}>{ownPublishing ? plansCta : t('freePreview.startNowLink')}</button>;
  // Verification can finish while this board stays mounted. Continue the same
  // full interview only from the explicit Free preview entry, never normal /app.
  if (location.pathname === '/app' && new URLSearchParams(location.search).get('preview') === '1'
    && freePreview.mode === 'active' && !freePreview.readOnly && ownPublishing
    && onboardingDestination === FREE_ONBOARDING_PATH
    && (brand.status === 'missing' || (brand.status === 'present' && state.key === 'BB'))) {
    return <Navigate to={FREE_ONBOARDING_PATH} replace />;
  }
  if (loading) return <section className="card fpb-board" role="status" aria-busy="true"><Eyebrow t={t} /><div className="sj-sk" style={{ width: '60%' }} /></section>;
  if (state.key === 'BV') return <section className="card fpb-board fpb-state" data-state="BV">
    <Eyebrow t={t} /><h2 className="fpb-h">{t('freePreview.verifyTitle')}</h2><p>{t(isFirstPostOnboarding(freePreview.entitlements) ? 'firstPostOnboarding.verifyBody' : 'freePreview.verifyBody', { email: user?.email || '' })}</p>
    <div className="fpb-next-row"><button type="button" className="btn btn-primary" disabled={sending || sent} onClick={resend}>{sent ? t('freePreview.verifySent') : t('freePreview.verifyResend')}</button>{startNow}</div>
  </section>;
  if (state.key === 'B0') return <section className="card fpb-board fpb-state" data-state="B0">
    <Eyebrow t={t} /><h2 className="fpb-h">{t('freePreview.noBrandTitle')}</h2><p>{t('freePreview.noBrandBody')}</p>
    {onboardingDestination && <button type="button" className="btn btn-primary" onClick={continueOnboarding}>{t('freePreview.noBrandCta')}</button>}
  </section>;
  if (state.key === 'BB') return <section className="card fpb-board fpb-state" data-state="BB">
    <Eyebrow t={t} /><h2 className="fpb-h">{t('freePreview.brandIncompleteTitle')}</h2><p>{t('freePreview.brandIncompleteBody')}</p>
    <div className="fpb-next-row">{onboardingDestination && <button type="button" className="btn btn-primary" onClick={continueOnboarding}>{t('freePreview.brandIncompleteCta')}</button>}{startNow}</div>
  </section>;
  const running = !state.draft1Ready;
  const placeholders = Math.max(0, limit - drafts.length);
  return <div className="fpb-board-wrap" data-state={state.key}>
    <section className="card fpb-board">
      <header className="fpb-head">
        <Eyebrow t={t} />
        {state.draft1Ready ? <h2 className="fpb-h">{t('freePreview.readyTitle', { brand: brandName || t('reveal.yourBrand') })}</h2> : <h2 className="fpb-h">{t('freePreview.runningTitle')}</h2>}
        {returning && <p className="fpb-muted">{t('freePreview.welcomeBack')}</p>}
        {running && state.key !== 'B7' && state.key !== 'B8' && <div className="fpb-steps" role="status"><span className="sj-live"><i className="sj-live-d" /> {t(`freePreview.${STEP_KEYS[state.step - 1]}`)}</span><span className="fpb-muted fpb-small">{t('freePreview.stepOf', { n: state.step, total: 4 })}</span></div>}
        {state.key === 'B7' && <div className="fpb-note" role="status"><strong>{t('freePreview.slowTitle')}</strong> {t('freePreview.slowBody')}</div>}
        {state.key === 'B8' && <div className="fpb-note" role="status"><strong>{t('freePreview.failedTitle')}</strong> {t('freePreview.failedBody')}<div className="fpb-next-row"><button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/app/help')}>{t('freePreview.contactSupport')}</button><button type="button" className="btn btn-ghost btn-sm" onClick={() => openWall('cta')}>{plansCta}</button></div></div>}
      </header>
      <div className="fpb-drafts-head"><div><h3 className="fpb-card-h">{t('freePreview.draftsTitle')}</h3><p className="fpb-muted fpb-small">{t('freePreview.draftsNote')}</p></div><span className="fpb-muted fpb-small">{t('freePreview.draftsCount', { used: state.readyCount, limit })}</span></div>
      <div className="fpb-drafts" role="list">
        {drafts.slice(0, limit).map((draft, index) => <div role="listitem" key={draft.id}><DraftCard draft={draft} index={index} t={t} onOpen={openDraft} /></div>)}
        {Array.from({ length: placeholders }).map((_, i) => <div role="listitem" key={`pending-${i}`}><DraftCard draft={null} t={t} failed={state.key === 'B8'} /></div>)}
      </div>
      {state.key === 'B5' && ideas.length > 0 && <section className="fpb-ideas" aria-labelledby="fpb-plan-title"><h3 id="fpb-plan-title" className="fpb-card-h">{t('freePreview.shortPlanTitle')}</h3><ol className="fpb-plan-list">{ideas.map((idea) => <li key={idea.index}>
        <div>{idea.day && <span className="pill">{idea.day}</span>}<span>{idea.title}</span></div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { trackPreview('preview_idea_clicked', { ideaIndex: idea.index }); openWall('generate', { ideaIndex: idea.index }); }}><Icon name="lock" size={11} /> {t('freePreview.ideaCta')}</button>
      </li>)}</ol></section>}
      {state.draft1Ready && <div className="fpb-next-step"><div className="fpb-next-row"><button type="button" className="btn btn-primary" onClick={connect}>{t('finishSetup.socialCta')}</button><div className="fpb-trial-option"><button type="button" className="fpb-link" onClick={() => openWall('cta')}>{plansCta}</button>{!ownPublishing && <p className="fpb-muted fpb-small">{t('freePreview.terms')}</p>}</div></div></div>}
      <details className="fpb-context"><summary>{t('freePreview.contextTitle')}</summary><div className="fpb-report"><SiteCard facts={facts} t={t} /><MarketCard snapshot={state.snapshot} report={research.report} state={state} t={t} onOpen={openSnapshot} /></div></details>
    </section>
  </div>;
}
